import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Trash2, Tag, Truck, Store, Check, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader } from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { EmbeddedCheckoutPanel } from "../components/EmbeddedCheckoutPanel";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const SHIP_FEE = 15;
const EMPTY_ADDR = { name: "", line1: "", line2: "", city: "", state: "", zip: "", phone: "" };
const CART_KEY = "iri_shop_cart";
const readCart = () => { try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch { return {}; } };

export default function Shop() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(readCart);
  const [activeCat, setActiveCat] = useState("all");
  const [stage, setStage] = useState("browse"); // browse | details | pay
  const [clientSecret, setClientSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", promo: "", fulfillment: "pickup", address: EMPTY_ADDR });

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" })); }, [user]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/shop/order-status/${sessionId}`);
        if (data.payment_status === "paid") { setResult(data); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) setTimeout(poll, 1500);
      else setResult({ pending: true });
    };
    poll();
  }, [sessionId]);

  const add = (slug) => setCart((c) => ({ ...c, [slug]: (c[slug] || 0) + 1 }));
  const dec = (slug) => setCart((c) => { const q = (c[slug] || 0) - 1; const n = { ...c }; if (q <= 0) delete n[slug]; else n[slug] = q; return n; });
  const removeItem = (slug) => setCart((c) => { const n = { ...c }; delete n[slug]; return n; });

  const lines = useMemo(() =>
    Object.entries(cart).map(([slug, qty]) => {
      const p = products.find((x) => x.slug === slug);
      return p ? { ...p, qty } : null;
    }).filter(Boolean), [cart, products]);
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const shipping = form.fulfillment === "ship" ? SHIP_FEE : 0;
  const count = lines.reduce((s, l) => s + l.qty, 0);

  const pay = async () => {
    if (!form.email.trim()) { toast.error("Please enter your email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/public-checkout", {
        items: lines.map((l) => ({ slug: l.slug, quantity: l.qty })),
        origin_url: window.location.origin,
        email: form.email, name: form.name, promo_code: form.promo || null,
        fulfillment: form.fulfillment,
        shipping_address: form.fulfillment === "ship" ? form.address : null,
      });
      setClientSecret(data.client_secret);
      setStage("pay");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout"); }
    finally { setBusy(false); }
  };

  // --- Order confirmation ---
  if (sessionId) {
    return (
      <div data-testid="shop-success-page">
        <Seo title="Order Confirmed" path="/shop" />
        <PageHeader overline="Skincare Shop" title="Thank you for your order" subtitle="A confirmation is on its way to your inbox." />
        <div className="max-w-lg mx-auto px-6 pb-24 text-center">
          {!result ? (
            <div className="flex flex-col items-center gap-3 text-[#8A9492]"><Loader2 className="h-6 w-6 animate-spin" /> Confirming your payment…</div>
          ) : result.pending ? (
            <p className="text-[#5C6664]">Your payment is processing — you'll get an email confirmation shortly.</p>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 p-8" data-testid="shop-confirmed">
              <div className="mx-auto h-12 w-12 rounded-full iri-gradient flex items-center justify-center mb-4"><Check className="h-6 w-6 text-ink" /></div>
              <p className="text-[#5C6664]">Your order is confirmed{result.fulfillment === "ship" ? " and will ship to your address" : " and will be ready for pickup at the spa"}. We've emailed your receipt.</p>
            </div>
          )}
          <Link to="/shop" className="mt-6 inline-flex items-center gap-1 text-sm text-[#8A9492] hover:text-ink" data-testid="shop-continue">
            <ArrowLeft className="h-4 w-4" /> Continue shopping
          </Link>
        </div>
      </div>
    );
  }

  // --- Embedded payment ---
  if (stage === "pay" && clientSecret) {
    return (
      <div data-testid="shop-pay-page">
        <Seo title="Checkout" path="/shop" />
        <PageHeader overline="Skincare Shop" title="Complete your purchase" subtitle="Secure payment — you never leave our site." />
        <div className="max-w-2xl mx-auto px-6 pb-24">
          <EmbeddedCheckoutPanel clientSecret={clientSecret} onBack={() => { setClientSecret(null); setStage("details"); }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="shop-page">
      <Seo title="Shop Medical-Grade Skincare" description="Shop DermaGenix Lab medical-grade skincare from Iridescent Medical & Wellness Spa. Ships to you or pick up in Clinton Township, MI." path="/shop" />
      <PageHeader overline="Skincare Shop" title="Medical-grade products" subtitle="Professional formulas, available to everyone. Pick up in-spa or ship to your door." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-3 gap-10 items-start">
          {/* products */}
          <div className="lg:col-span-2">
            {(() => {
              const cats = ["all", ...Array.from(new Set(products.map((p) => p.category)))];
              const visible = activeCat === "all" ? products : products.filter((p) => p.category === activeCat);
              const sections = [
                ["Skin Care", visible.filter((p) => p.brand !== "Nutrafol")],
                ["Hair Care", visible.filter((p) => p.brand === "Nutrafol")],
              ].filter(([, items]) => items.length > 0);
              return (
                <>
                  {cats.length > 2 && (
                    <div className="flex flex-wrap gap-2 mb-8" data-testid="shop-filters">
                      {cats.map((c) => (
                        <button
                          key={c}
                          onClick={() => setActiveCat(c)}
                          data-testid={`shop-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`px-4 py-2 rounded-full text-sm transition-colors ${activeCat === c ? "iri-gradient text-ink font-medium" : "border border-slate-200 text-[#5C6664] hover:border-slate-300"}`}
                        >
                          {c === "all" ? "All products" : c}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="space-y-12">
                    {sections.map(([section, items]) => (
                      <div key={section} data-testid={`shop-section-${section.toLowerCase().replace(/\s+/g, "-")}`}>
                        <h2 className="font-heading text-2xl font-light text-ink mb-6">{section}</h2>
                        <div className="grid sm:grid-cols-2 gap-6" data-testid="shop-products">
                          {items.map((p) => (
                            <div key={p.slug} className="bg-white rounded-2xl border border-slate-100 overflow-hidden flex flex-col" data-testid={`shop-product-${p.slug}`}>
                              <Link to={`/shop/${p.slug}`} className="aspect-square bg-stone overflow-hidden block" data-testid={`shop-product-link-${p.slug}`}>
                                <img src={p.image} alt={p.name} className="w-full h-full object-contain hover:scale-105 transition-transform duration-500" loading="lazy" />
                              </Link>
                              <div className="p-5 flex flex-col flex-1">
                                <p className="text-[11px] uppercase tracking-wider text-iri-mid">{p.category}</p>
                                <Link to={`/shop/${p.slug}`} className="text-sm font-medium text-ink mt-1 leading-snug flex-1 hover:text-iri-mid transition-colors">{p.name}</Link>
                                <div className="flex items-center justify-between mt-3">
                                  <span className="font-heading text-xl font-light text-ink">{money(p.price)}</span>
                                  {p.in_stock ? (
                                    cart[p.slug] ? (
                                      <div className="flex items-center gap-2">
                                        <button onClick={() => dec(p.slug)} data-testid={`shop-dec-${p.slug}`} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-stone"><Minus className="h-3.5 w-3.5" /></button>
                                        <span className="w-5 text-center text-sm" data-testid={`shop-qty-${p.slug}`}>{cart[p.slug]}</span>
                                        <button onClick={() => add(p.slug)} data-testid={`shop-inc-${p.slug}`} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-stone"><Plus className="h-3.5 w-3.5" /></button>
                                      </div>
                                    ) : (
                                      <Button onClick={() => add(p.slug)} data-testid={`shop-add-${p.slug}`} className="rounded-full h-9 px-4 bg-ink text-white hover:bg-ink/90 gap-1.5 text-sm"><Plus className="h-3.5 w-3.5" /> Add</Button>
                                    )
                                  ) : (
                                    <span className="text-xs text-[#8A9492]" data-testid={`shop-oos-${p.slug}`}>Out of stock</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* cart */}
          <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-slate-100 p-6" data-testid="shop-cart">
            <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
              <ShoppingBag className="h-5 w-5 text-iri-mid" />
              <h2 className="font-heading text-lg font-medium text-ink">Your cart{count > 0 ? ` (${count})` : ""}</h2>
            </div>

            {lines.length === 0 ? (
              <p className="text-sm text-[#8A9492] py-8 text-center" data-testid="shop-cart-empty">Your cart is empty. Add a product to get started.</p>
            ) : (
              <>
                <div className="divide-y divide-slate-100 max-h-72 overflow-auto">
                  {lines.map((l) => (
                    <div key={l.slug} className="py-3 flex items-center gap-3" data-testid={`cart-line-${l.slug}`}>
                      <img src={l.image} alt="" className="h-12 w-12 rounded-lg object-cover bg-stone shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-ink truncate">{l.name}</p>
                        <p className="text-xs text-[#8A9492]">{l.qty} × {money(l.price)}</p>
                      </div>
                      <button onClick={() => removeItem(l.slug)} data-testid={`cart-remove-${l.slug}`} className="text-[#8A9492] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>

                {stage === "details" && (
                  <div className="mt-4 space-y-3 pt-4 border-t border-slate-100" data-testid="shop-details">
                    <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shop-name" className="rounded-lg h-11" />
                    <Input type="email" placeholder="Email (for your receipt)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="shop-email" className="rounded-lg h-11" />
                    <div className="grid grid-cols-2 gap-2">
                      {[["pickup", Store, "Pick up"], ["ship", Truck, "Ship (+$15)"]].map(([val, Icon, label]) => (
                        <button key={val} onClick={() => setForm({ ...form, fulfillment: val })} data-testid={`shop-fulfillment-${val}`}
                          className={`flex items-center justify-center gap-1.5 rounded-lg h-10 text-sm border ${form.fulfillment === val ? "iri-gradient text-ink border-transparent font-medium" : "border-slate-200 text-[#5C6664]"}`}>
                          <Icon className="h-4 w-4" /> {label}
                        </button>
                      ))}
                    </div>
                    {form.fulfillment === "ship" && (
                      <div className="space-y-2" data-testid="shop-address">
                        <Input placeholder="Address line 1" value={form.address.line1} onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value, name: form.name } })} data-testid="shop-addr-line1" className="rounded-lg h-10" />
                        <div className="grid grid-cols-3 gap-2">
                          <Input placeholder="City" value={form.address.city} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} data-testid="shop-addr-city" className="rounded-lg h-10" />
                          <Input placeholder="State" value={form.address.state} onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })} data-testid="shop-addr-state" className="rounded-lg h-10" />
                          <Input placeholder="ZIP" value={form.address.zip} onChange={(e) => setForm({ ...form, address: { ...form.address, zip: e.target.value } })} data-testid="shop-addr-zip" className="rounded-lg h-10" />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-[#8A9492]" />
                      <Input placeholder="Promo code (optional)" value={form.promo} onChange={(e) => setForm({ ...form, promo: e.target.value.toUpperCase() })} data-testid="shop-promo" className="rounded-lg h-10" />
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-sm">
                  <div className="flex justify-between text-[#5C6664]"><span>Subtotal</span><span data-testid="shop-subtotal">{money(subtotal)}</span></div>
                  {shipping > 0 && <div className="flex justify-between text-[#5C6664]"><span>Shipping</span><span>{money(shipping)}</span></div>}
                  <div className="flex justify-between text-ink font-medium pt-1"><span>Total</span><span data-testid="shop-total">{money(subtotal + shipping)}</span></div>
                </div>

                {stage === "browse" ? (
                  <Button onClick={() => setStage("details")} data-testid="shop-checkout-btn" className="w-full mt-4 iri-gradient text-ink rounded-full h-12 font-medium">Checkout</Button>
                ) : (
                  <Button onClick={pay} disabled={busy} data-testid="shop-pay-btn" className="w-full mt-4 iri-gradient text-ink rounded-full h-12 font-medium">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to secure payment"}
                  </Button>
                )}
                <p className="text-[11px] text-[#8A9492] text-center mt-2">No account required · secure Stripe checkout</p>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
